"use client";

import { useUserChoices } from "@/state/globalStore";
import {
  AssignSituationStrategy,
  ConfirmThoughtsRequest,
  DeleteThoughtsRequest,
  MeiliSuggestedThought,
  MeiliThought,
  MindstringPrototype,
  ResetReflectionsRequest,
} from "@/generated/fastapi";
import { useMeiliRuntime } from "@/core/search";
import { useSituationsQuery } from "@/features/situations";
import {
  useConfirmThoughtsMutation,
  useDeleteThoughtsMutation,
  useResetReflectionsMutation,
  useThoughtRefresh,
} from "@/features/thoughts";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import type { ConfirmationDialogController } from "@/shared/ui/useConfirmationDialog";
import { useCallback, useMemo } from "react";

type DeleteThoughtsOptions = {
  skipConfirmation?: boolean;
};

// ------------------------------------------------------------------
// Utils: Flags unassigned thoughts
// ------------------------------------------------------------------

export default function useStudioThoughtSelectionActions({
  confirmSelectedThoughtsDeletion,
  confirmSelectedSuggestedThoughtsDeletion,
  confirmSelectedThoughtsResetReflections,
  confirmSelectedThoughtsConfirmation,
  setConfirmSelectedThoughtPrototypes,
}: {
  // If not provided, deletion/reset will be executed directly without confirmation dialog
  confirmSelectedThoughtsDeletion?: ConfirmationDialogController;
  confirmSelectedSuggestedThoughtsDeletion?: ConfirmationDialogController;
  confirmSelectedThoughtsResetReflections?: ConfirmationDialogController;
  confirmSelectedThoughtsConfirmation?: ConfirmationDialogController<{
    situationId?: string;
    assignStrategy?: AssignSituationStrategy;
  }>;
  setConfirmSelectedThoughtPrototypes?: (
    prototypes: MindstringPrototype[],
  ) => void;
}) {
  const {
    unselectThoughts,
    resetSelectedThoughts,
    resetSelectedSuggestedThoughts,
  } = useUserChoices();
  const situationsQuery = useSituationsQuery();
  const { searchThoughtDocuments } = useMeiliRuntime();
  const deleteThoughtsMutation = useDeleteThoughtsMutation();
  const resetReflectionsMutation = useResetReflectionsMutation();
  const confirmThoughtsMutation = useConfirmThoughtsMutation();
  const {
    refreshReflectionSurfaces,
    syncDeletedThoughts,
    syncSuggestedThoughtConfirmation,
  } = useThoughtRefresh();

  // Thoughts: Delete (Memoized, since called in Loops)
  const handleSelectedThoughtsDeleteClick = useCallback(
    async (
      request: DeleteThoughtsRequest,
      onSuccess?: () => void,
      affectedPrototypes?: MindstringPrototype[],
      options?: DeleteThoughtsOptions,
    ) => {
      const executeDelete = async () => {
        try {
          const response = await deleteThoughtsMutation.mutateAsync(request);
          if (response.status !== "success" && response.status !== "pending") {
            showErrorToast(response.message || "Failed to delete thoughts");
            return;
          }

          unselectThoughts(request.thought_ids);
          syncDeletedThoughts({
            response,
            indexName: request.sync_meili_index,
            prototypes: affectedPrototypes,
          });
          onSuccess?.(); // call success callback if provided
          if (response.status === "pending") {
            showWarningToast(
              response.message ||
                "Thoughts removed, but search is still catching up.",
            );
          } else {
            showSuccessToast("Thoughts deleted successfully");
          }
        } catch {
          showErrorToast("Failed to delete thoughts");
        }
      };

      // If dialog is provided, show confirmation. Otherwise execute directly
      if (confirmSelectedThoughtsDeletion && !options?.skipConfirmation) {
        confirmSelectedThoughtsDeletion.onOpen(executeDelete);
      } else {
        await executeDelete();
      }
    },
    [
      deleteThoughtsMutation,
      unselectThoughts,
      confirmSelectedThoughtsDeletion,
      syncDeletedThoughts,
    ],
  );

  // Thoughts: Reset Reflections
  const handlSelectedThoughtsResetReflectionsClick = useCallback(
    async (request: ResetReflectionsRequest) => {
      const executeReset = async () => {
        try {
          await resetReflectionsMutation.mutateAsync(request);
          refreshReflectionSurfaces();
          showSuccessToast("Reflections reset successfully");
        } catch {
          showErrorToast("Failed to reset reflections");
        }
      };

      // If dialog is provided, show confirmation. Otherwise execute directly
      if (confirmSelectedThoughtsResetReflections) {
        confirmSelectedThoughtsResetReflections.onOpen(executeReset);
      } else {
        await executeReset();
      }
    },
    [
      confirmSelectedThoughtsResetReflections,
      resetReflectionsMutation,
      refreshReflectionSurfaces,
    ],
  );

  // Thoughts: Unselect
  const handleSelectedThoughtsUnselectClick = useCallback(async () => {
    resetSelectedThoughts();
    // resetSelectedSituations();
  }, [resetSelectedThoughts]);

  // Suggested Thoughts: Unselect
  const handleSelectedSuggestedThoughtsUnselectClick = useCallback(async () => {
    resetSelectedSuggestedThoughts();
  }, [resetSelectedSuggestedThoughts]);

  // Suggested Thoughts: Delete (Memoized, since called in Loops)
  const handleSelectedSuggestedThoughtsDeleteClick = useCallback(
    async (
      request: DeleteThoughtsRequest,
      onSuccess?: () => void,
      affectedPrototypes?: MindstringPrototype[],
    ) => {
      const executeDelete = async () => {
        try {
          const response = await deleteThoughtsMutation.mutateAsync(request);
          if (response.status !== "success" && response.status !== "pending") {
            showErrorToast(
              response.message || "Failed to delete suggested thoughts",
            );
            return;
          }

          syncDeletedThoughts({
            response,
            indexName: request.sync_meili_index,
            prototypes: affectedPrototypes,
          });
          onSuccess?.(); // call success callback if provided
          if (response.status === "pending") {
            showWarningToast(
              response.message ||
                "Suggested thoughts removed, but search is still catching up.",
            );
          } else {
            showSuccessToast("Suggested thoughts deleted successfully"); // inform user of success
          }
        } catch {
          showErrorToast("Failed to delete suggested thoughts");
        }
      };

      if (confirmSelectedSuggestedThoughtsDeletion) {
        confirmSelectedSuggestedThoughtsDeletion.onOpen(executeDelete);
      } else {
        await executeDelete();
      }
    },
    [
      confirmSelectedSuggestedThoughtsDeletion,
      deleteThoughtsMutation,
      syncDeletedThoughts,
    ],
  );

  // Suggested Thoughts: Confirm Memoized
  const handleSelectedSuggestedThoughtsConfirmClick = useCallback(
    async ({
      request,
      onSuccess,
    }: {
      request: ConfirmThoughtsRequest;
      onSuccess?: () => void;
    }) => {
      // Define the actual confirm execution function
      const executeConfirm = async (dynamicValues?: {
        situationId?: string;
        assignStrategy?: AssignSituationStrategy;
      }) => {
        // Build final request with dynamic values if provided
        const finalRequest: ConfirmThoughtsRequest = {
          ...request,
          assign_situation_id:
            dynamicValues?.situationId || request.assign_situation_id,
          assign_situation_strategy:
            dynamicValues?.assignStrategy || request.assign_situation_strategy,
        };

        const confirmationResponse =
          await confirmThoughtsMutation.mutateAsync(finalRequest);
        if (
          confirmationResponse &&
          (confirmationResponse.status === "success" ||
            confirmationResponse.status === "pending")
        ) {
          onSuccess?.();
          if (finalRequest.reset_reflections) {
            refreshReflectionSurfaces();
          }
          syncSuggestedThoughtConfirmation(
            confirmationResponse,
            affectedPrototypes,
          );

          if (confirmationResponse.status === "pending") {
            showWarningToast(
              confirmationResponse.message ||
                "Thoughts confirmed, but search is still catching up.",
            );
          } else {
            showSuccessToast("Thoughts confirmed in your profile");
          }
        } else if (
          confirmationResponse &&
          confirmationResponse.status === "not_found"
        ) {
          onSuccess?.();
          showErrorToast(`Not successfull: ${confirmationResponse.message}`);
        } else if (
          confirmationResponse &&
          confirmationResponse.status === "validation_error"
        ) {
          onSuccess?.();
          showErrorToast(
            confirmationResponse.message || "Failed to confirm suggested thoughts",
          );
        } else {
          showErrorToast("Failed to confirm suggested thoughts"); // generic error
        }
      };

      const searchResults = await searchThoughtDocuments<MeiliSuggestedThought>({
        indexName: "suggested_thoughts",
        items_ids: request.thought_ids,
      });
      const affectedPrototypes = Array.from(
        new Set(
          searchResults.map(
            (thought) => thought.prototype as MindstringPrototype,
          ),
        ),
      );

      const validSituationIds = situationsQuery.isSuccess
        ? new Set(
            (situationsQuery.data ?? []).map((situation) => situation.id),
          )
        : null;
      const isAnyNeedingAnchor = searchResults.some(
        (thought) =>
          !thought.situation_id ||
          (validSituationIds !== null &&
            !validSituationIds.has(thought.situation_id)),
      );
      if (isAnyNeedingAnchor && !confirmSelectedThoughtsConfirmation) {
        // For developer: Must Implement confirmation dialog and situation assignment UI if flag is true
        throw new Error(
          "Thought is not anchored to a situation; provide confirmation dialog to proceed.",
        );
      }

      // If dialog is provided, show confirmation. Otherwise execute directly
      if (isAnyNeedingAnchor && confirmSelectedThoughtsConfirmation) {
        setConfirmSelectedThoughtPrototypes?.(affectedPrototypes);
        confirmSelectedThoughtsConfirmation.onOpen((dynamicValues) =>
          executeConfirm(dynamicValues),
        );
      } else {
        await executeConfirm();
      }
    },
    [
      confirmSelectedThoughtsConfirmation,
      confirmThoughtsMutation,
      refreshReflectionSurfaces,
      searchThoughtDocuments,
      setConfirmSelectedThoughtPrototypes,
      syncSuggestedThoughtConfirmation,
      situationsQuery.data,
      situationsQuery.isSuccess,
    ],
  );

  // Clipboard: Copy Mindstrings of Selected Thoughts to Clipboard
  const handleCopyMindstringsToClipboard = useCallback(
    async (
      selectedIds: string[],
      indexName: "thoughts" | "suggested_thoughts",
    ) => {
      // Step: fetch data from Meili for these ids
      //!!!! DO I NEED THIS? I COULD JUST GET IT FROM FASTAPI  BACKEND OR>>>> is it faster to get from meili?
      const searchResults = await searchThoughtDocuments<MeiliThought>({
        indexName,
        items_ids: selectedIds,
      });
      // Step: get list of mindstrings from results
      const mindstringsToCopy = searchResults.map((item) => item.mindstring);

      // Step: copy to clipboard as JSON array string
      await navigator.clipboard.writeText(JSON.stringify(mindstringsToCopy));
      showSuccessToast("Thoughts copied to clipboard");
    },
    [searchThoughtDocuments],
  );

  return useMemo(
    () => ({
      // Thoughts
      handleSelectedThoughtsDeleteClick,
      handlSelectedThoughtsResetReflectionsClick,
      handleSelectedThoughtsUnselectClick,
      // Suggested Thoughts
      handleSelectedSuggestedThoughtsUnselectClick,
      handleSelectedSuggestedThoughtsDeleteClick,
      handleSelectedSuggestedThoughtsConfirmClick,
      // Clipboard
      handleCopyMindstringsToClipboard,
    }),
    [
      handleSelectedThoughtsDeleteClick,
      handlSelectedThoughtsResetReflectionsClick,
      handleSelectedThoughtsUnselectClick,
      handleSelectedSuggestedThoughtsUnselectClick,
      handleSelectedSuggestedThoughtsDeleteClick,
      handleSelectedSuggestedThoughtsConfirmClick,
      handleCopyMindstringsToClipboard,
    ],
  );
}
